import { insightsClient } from '@algolia/client-insights';

import useAlgoliaUserToken from './user-token';

const client = insightsClient(
    import.meta.env.VITE_ALGOLIA_APPLICATION_ID,
    import.meta.env.VITE_ALGOLIA_API_INSIGHTS_KEY,
    import.meta.env.VITE_ALGOLIA_API_INSIGHTS_REGION,
);

const ALGOLIA_CONTEXT_QUERY_PARAM = 'actx';
/**
 * @param controller Stimulus controller
 * @param contextName Used to prefix event names
 */
export default (
    controller,
    contextName = 'Page',
) => {
    /**
     * Algolia only supports ASCI characters in event names. Events names are also limited to 64 characters.
     *
     * @param name
     * @returns {string}
     */
    const formatEventName = (name) => {
        const contextualizedName = contextName ? `${contextName} - ${name}` : name;

        // https://www.30secondsofcode.org/js/s/remove-accents/
        return contextualizedName.normalize('NFD').replace(/[\u0300-\u036f]/g, '').slice(0, 64);
    };

    const prepareEvent = (eventType, params) => {
        let event = {
            eventType,
            eventName: formatEventName(params.eventName),
            index: params.index,
            objectIDs: params.objectIds.toString().split(',') || [],
            queryID: params.queryId,
            timestamp: Date.now(),
            userToken: controller.getAnonymousUserToken(),
        };

        if (controller.hasAuthenticatedUserToken()) {
            const authenticatedUserToken = controller.getAuthenticatedUserToken();

            event = {
                ...event,
                authenticatedUserToken: authenticatedUserToken,
                userToken: authenticatedUserToken,
            };
        }

        return event;
    };

    const addAlgoliaContextToUrl = (target, clickEvent, contentType) => {
        const url = new URL(target.href);
        const algoliaContext = {
            index: clickEvent.index,
            objectIDs: clickEvent.objectIDs,
            queryID: clickEvent.queryID,
            contentType,
        };

        url.searchParams.set(ALGOLIA_CONTEXT_QUERY_PARAM, window.btoa(JSON.stringify(algoliaContext)));

        return url;
    };

    const getAlgoliaContextFromUrl = () => {
        const url = new URL(window.location);
        const algoliaContext = url.searchParams.has(ALGOLIA_CONTEXT_QUERY_PARAM)
            ? JSON.parse(window.atob(url.searchParams.get(ALGOLIA_CONTEXT_QUERY_PARAM)))
            : null;

        if (!algoliaContext) {
            return null;
        }

        return {
            index: algoliaContext?.index || '',
            objectIds: algoliaContext?.objectIDs || '',
            queryId: algoliaContext?.queryID || '',
            contentType: algoliaContext?.contentType || '',
        };
    };

    const sendClickInsight = async (event) => {
        const target = event.currentTarget;
        const params = event.params;

        const algoliaEvent = prepareEvent('click', params);

        algoliaEvent.positions = params.positions.toString().split(',').map(Number) || [];

        try {
            await client.pushEvents(
                {
                    events: [algoliaEvent],
                },
                {
                    timeouts: {
                        write: 500,
                    },
                },
            );
        } catch (error) {
            // eslint-disable-next-line no-console
            console.warn('Insights event not sent: timeout or maximum retries reached', error);
        }

        controller.dispatch('insightSent', { target });

        if (target.href) {
            const url = addAlgoliaContextToUrl(target, algoliaEvent, params.contentType);

            window.location = url.href;
        }
    };

    const sendConversionInsight = async (event) => {
        const target = event.currentTarget;
        const params = event.params;

        const algoliaEvent = prepareEvent('conversion', params);

        try {
            await client.pushEvents(
                {
                    events: [algoliaEvent],
                },
                {
                    timeouts: {
                        write: 500,
                    },
                },
            );
        } catch (error) {
            // eslint-disable-next-line no-console
            console.warn('Insights event not sent: timeout or maximum retries reached', error);
        }

        controller.dispatch('insightSent', { target });

        if (target.href) {
            window.location = target.href;
        }
    };

    useAlgoliaUserToken(controller);

    Object.assign(controller, {
        sendClickInsight,
        sendConversionInsight,
        getAlgoliaContextFromUrl,
    });
};
